import React from 'react';
import { type ImageCardVariantProps } from './image-card.variants';
import {
  type ImageCardFetchPriority,
  type ImageCardLoading,
} from './image-card.types';
import { ImageCardDefault } from './ImageCardDefault';
import { ImageCardFullWidth } from './ImageCardFullWidth';

export type {
  ImageCardFetchPriority,
  ImageCardLoading,
} from './image-card.types';

export type ImageCardProps = {
  /** Image source URL */
  imageUrl: string;
  /** Alt text for the image */
  imageAlt: string;
  /** Optional styling for the image */
  imgClass?: string;
  /** Background color behind the image (default variant only; prefer over imgClass) */
  imageBackgroundColor?: 'neutral-50' | 'neutral-100' | 'white' | 'transparent';
  /** Aspect ratio of the image area (default variant only; prefer over imgClass) */
  imageAspect?: 'default' | 'video' | 'square';
  /** Defines the content area under the image and supports any valid React node (text, buttons, etc.) */
  children: React.ReactNode;
  /** Visual variant of the card */
  variant?: ImageCardVariantProps['variant'];
  /** full-width only: horizontal alignment of overlay children */
  align?: 'left' | 'center';
  /** Optional configuration props (e.g., className, aria-*) for styling and accessibility */
  className?: string;
  /**
   * When the image starts downloading. Defaults to `lazy`. Does not change
   * `fetchPriority`.
   */
  loading?: ImageCardLoading;
  /**
   * Download urgency relative to other images. Omitted by default.
   * Does not change `loading`.
   */
  fetchPriority?: ImageCardFetchPriority;
};

export const ImageCard: React.FC<ImageCardProps> = ({
  imageUrl,
  imageAlt,
  imgClass,
  imageBackgroundColor,
  imageAspect,
  children,
  variant = 'default',
  align = 'center',
  className,
  loading = 'lazy',
  fetchPriority,
}) => {
  if (variant === 'full-width') {
    return (
      <ImageCardFullWidth
        imageUrl={imageUrl}
        imageAlt={imageAlt}
        imgClass={imgClass}
        align={align}
        className={className}
        loading={loading}
        fetchPriority={fetchPriority}
      >
        {children}
      </ImageCardFullWidth>
    );
  }

  return (
    <ImageCardDefault
      imageUrl={imageUrl}
      imageAlt={imageAlt}
      imgClass={imgClass}
      imageBackgroundColor={imageBackgroundColor}
      imageAspect={imageAspect}
      className={className}
      loading={loading}
      fetchPriority={fetchPriority}
    >
      {children}
    </ImageCardDefault>
  );
};
